import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { abbottProducts, lewisProducts, productList } from "./ProductList";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/AdminDashboard.css";

// Company definitions
const companies = [
  { 
    id: 1, 
    name: "Abbott", 
    logo: "../img/Default_Company/Abbott-logo-new.png",
    products: abbottProducts
  },
  { 
    id: 2, 
    name: "Levis", 
    logo: "../img/Company_B/logo-B-2.png",
    products: lewisProducts
  }
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("products");
  const [modalImage, setModalImage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [qrCodeForm, setQrCodeForm] = useState({
    qrImage: "",
    productId: "",
    companyId: "",
    codeType: "qrcode" // Default to QR code
  });
  const [qrCodes, setQrCodes] = useState([]);
  const [qrCodeSearch, setQrCodeSearch] = useState("");
  const [qrCodeFilter, setQrCodeFilter] = useState("all"); // "all", "qrcode", or "datamatrix"
  const [selectedCompany, setSelectedCompany] = useState(companies[0]);
  const [products, setProducts] = useState(productList);
  const [filteredProducts, setFilteredProducts] = useState(companies[0].products);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [showLinkQrSection, setShowLinkQrSection] = useState(false);
  const [showConfigForm, setShowConfigForm] = useState(false);
  
  const handleLogout = () => {
    // Clear authentication status from both storages
    sessionStorage.removeItem('isAuthenticated');
    localStorage.removeItem('isAuthenticated');
    // Redirect to login page
    navigate('/admin');
  };

  // Filter products when company changes
  useEffect(() => {
    // For Abbott products (company ID 1), use the abbottProducts array directly
    // For other companies, filter based on the company property
    let filtered;
    if (selectedCompany.id === 1) {
      filtered = abbottProducts;
    } else {
      filtered = products.filter(product => 
        product.company === selectedCompany.id
      );
    }
    setFilteredProducts(filtered);
  }, [selectedCompany, products]);
  const [configurations, setConfigurations] = useState([
    {
      id: 1,
      companyName: "Abbott",
      themeColor: "#0d6efd",
      url: "abbott.h2cloud.it",
      logo: "../img/Default_Company/Abbott-logo-new.png",
      background: ""
    }
  ]);

  const [configForm, setConfigForm] = useState({
    companyName: "",
    themeColor: "#0d6efd",
    url: "",
    logo: "",
    background: ""
  });

  const [editingConfigId, setEditingConfigId] = useState(null);
  const [productForm, setProductForm] = useState({
    id: "",
    name: "",
    scanCountExceed: false,
    verified: true,
    quality: true,
    image: "",
    description: "",
    sublabel: "",
    gtin: "",
    lot: "",
    serial: "",
    expDate: "",
    calendar: "",
    leaflet: "",
    productPage: ""
  });

  const handleEdit = (index) => {
    setProductForm(products[index]);
  };

  const handleDelete = (index) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      const updatedProducts = products.filter((_, idx) => idx !== index);
      setProducts(updatedProducts);
    }
  };

  const handleConfigFormChange = (e) => {
    const { name, value } = e.target;
    setConfigForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setConfigForm(prev => ({
          ...prev,
          logo: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBackgroundFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setConfigForm(prev => ({
          ...prev,
          background: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleConfigEdit = (config) => {
    const subdomain = config.url.replace('.h2cloud.it', '');
    setConfigForm({
      companyName: config.companyName,
      themeColor: config.themeColor,
      url: subdomain,
      logo: config.logo,
      background: config.background
    });
    setEditingConfigId(config.id);
    setShowConfigForm(true); // Show the configuration form when edit button is clicked
  };

  const handleConfigDelete = (configId) => {
    if (window.confirm('Are you sure you want to delete this configuration?')) {
      setConfigurations(configurations.filter(config => config.id !== configId));
    }
  };

  const handleSetActive = (configId) => {
    setConfigurations(configurations.map(config => ({
      ...config,
      active: config.id === configId
    })));
  };

  const handleProductChange = (e) => {
    const { name, value } = e.target;
    setProductForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProductSubmit = (e) => {
    e.preventDefault();
    const newProduct = {
      ...productForm,
      id: products.length + 1,
      company: selectedCompany.id,
      verified: true,
      quality: true,
      scanCountExceed: false
    };
    
    setProducts([...products, newProduct]);
    setProductForm({
      id: "",
      name: "",
      scanCountExceed: false,
      verified: true,
      quality: true,
      image: "",
      description: "",
      sublabel: "",
      gtin: "",
      lot: "",
      serial: "",
      expDate: "",
      calendar: "",
      leaflet: "",
      productPage: ""
    });
  };

  const handleConfigSubmit = (e) => {
    e.preventDefault();
    const fullUrl = `${configForm.url.toLowerCase()}.h2cloud.it`;

    if (editingConfigId) {
      // Update existing config
      setConfigurations(configurations.map(config =>
        config.id === editingConfigId ? {
          ...config,
          companyName: configForm.companyName,
          themeColor: configForm.themeColor,
          url: fullUrl,
          logo: configForm.logo,
          background: configForm.background
        } : config
      ));
    } else {
      // Add new config
      const newConfig = {
        id: Date.now(),
        companyName: configForm.companyName,
        themeColor: configForm.themeColor,
        url: fullUrl,
        logo: configForm.logo,
        background: configForm.background
      };
      setConfigurations([...configurations, newConfig]);
    }

    // Reset form
    setConfigForm({
      companyName: "",
      themeColor: "#0d6efd",
      url: "",
      logo: "",
      background: ""
    });
    setEditingConfigId(null);
  };

  return (
    <>
    <div className="d-flex">
      {/* Left Navigation */}
      <div className="bg-left-nav text-white" style={{ width: "250px", minHeight: "100vh" }}>
        <div className="p-3">
          <div className="d-flex flex-column align-items-center p-1 pb-3 border-bottom">
            <div className="h2-logo-container">
              <div className="h2c-logo mb-2"></div>
            </div>
            <h4 className="h5 mb-0">Admin Dashboard</h4>
          </div>
        </div>
        <ul className="nav flex-column p-3 pt-0">
          <li className="nav-item mb-2">
            <button
              className={`nav-link btn btn-link text-white text-start w-100 ${activeSection === "theme" ? "active bg-primary" : ""}`}
              onClick={() => setActiveSection("theme")}
            >
              <i className="bi bi-palette me-2"></i>
             Configurations
            </button>
          </li>
          <li className="nav-item mb-2">
            <button
              className={`nav-link btn btn-link text-white text-start w-100 ${activeSection === "products" ? "active bg-primary" : ""}`}
              onClick={() => setActiveSection("products")}
            >
              <i className="bi bi-box me-2"></i>
              Products
            </button>
          </li>
          <li className="nav-item mb-2">
            <button
              className={`nav-link btn btn-link text-white text-start w-100 ${activeSection === "qrcode" ? "active bg-primary" : ""}`}
              onClick={() => setActiveSection("qrcode")}
            >
              <i className="bi bi-qr-code me-2"></i>
              QR Codes
            </button>
          </li>
          <hr className="my-3 bg-light opacity-25" />

          <li className="nav-item mt-2 mb-2">
            <Link to="/" className="nav-link btn btn-link text-white text-start w-100">
              <i className="bi bi-house-door me-2"></i>
              Back to Home
            </Link>
          </li>
          <li className="nav-item">
            <button
              className="nav-link btn btn-link text-danger text-start w-100"
              onClick={handleLogout}
            >
              <i className="bi bi-box-arrow-right me-2"></i>
              Logout
            </button>
          </li>
        </ul>
      </div>

      {/* Main Content */}
      <div className="flex-grow-1 p-4 admin-body">
        {activeSection === "products" && (
          <div>
            {/* Company Selector */}
            <div className="card mb-4 admin-card admin-company-selector">
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-building fs-4 text-primary me-2"></i>
                      <label className="form-label fw-bold mb-0">Select Company</label>
                    </div>
                    <select 
                      className="form-select form-select-lg shadow-sm"
                      value={selectedCompany.id}
                      onChange={(e) => {
                        const company = companies.find(c => c.id === parseInt(e.target.value));
                        setSelectedCompany(company);
                        setIsFormVisible(false); // Hide form when company changes
                      }}
                    >
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6 text-md-end mt-4 mt-md-0">
                    <div className="d-flex align-items-center justify-content-md-end">
                      <div className="admin-company-stats me-3">
                        <div className="text-muted small mb-1">
                          <i className="bi bi-box-seam me-1"></i>
                          Total Products
                        </div>
                        <div className="h3 mb-0">{filteredProducts.length}</div>
                      </div>
                      {selectedCompany.logo && (
                        <img 
                          src={selectedCompany.logo} 
                          alt={selectedCompany.name} 
                          className="admin-dashboard-company-logo"
                          style={{ height: '45px', width: 'auto' }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center">
                <h2 className="mb-0 me-3">Product Management</h2>
                <div className="admin-company-badge">{selectedCompany.name}</div>
              </div>
              <button 
                type="button" 
                className={`btn ${isFormVisible ? 'btn-secondary' : 'btn-primary'} d-flex align-items-center admin-add-button`}
                onClick={() => {
                  setIsFormVisible(!isFormVisible);
                  if (!isFormVisible) {
                    setProductForm({
                      id: "",
                      name: "",
                      scanCountExceed: false,
                      verified: true,
                      quality: true,
                      image: "",
                      description: "",
                      sublabel: "",
                      gtin: "",
                      lot: "",
                      serial: "",
                      expDate: "",
                      calendar: "",
                      leaflet: "",
                      productPage: ""
                    });
                  }
                }}
              >
                <i className={`bi bi-${isFormVisible ? 'x-lg' : 'plus-lg'} me-2`}></i>
                {isFormVisible ? 'Cancel' : 'Add New Product'}
              </button>
            </div>
            
            {/* Product Form */}
            <div className={`card mb-4 admin-card ${isFormVisible ? '' : 'd-none'}`}>
              <div className="card-header d-flex justify-content-between align-items-center admin-header">
                <div className="d-flex align-items-center">
                  <i className="bi bi-box-seam fs-4 me-2"></i>
                  <h5 className="card-title mb-0">Add New Product</h5>
                </div>
              </div>
              <div className="card-body admin-form-body">
                <form onSubmit={handleProductSubmit}>
                  {/* Basic Information */}
                  <div className="admin-form-section basic-info">
                    <div className="admin-section-title basic-info">
                      <i className="bi bi-info-circle"></i>
                      <h6 className="mb-0">Basic Information</h6>
                    </div>
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="admin-form-label">
                          <i className="bi bi-tag"></i>Product Name
                        </label>
                        <input
                          type="text"
                          className="form-control admin-form-control-lg"
                          name="name"
                          value={productForm.name}
                          onChange={handleProductChange}
                          placeholder="Enter product name"
                          required
                        />
                      </div>
                      <div className="col-12">
                        <label className="admin-form-label">
                          <i className="bi bi-file-text"></i>Description
                        </label>
                        <textarea
                          className="form-control admin-textarea"
                          name="description"
                          value={productForm.description}
                          onChange={handleProductChange}
                          rows="3"
                          placeholder="Enter detailed product description"
                          required
                          style={{ resize: 'vertical', minHeight: '100px' }}
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-semibold">
                          <i className="bi bi-bookmark me-2"></i>Sublabel
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="sublabel"
                          value={productForm.sublabel}
                          onChange={handleProductChange}
                          placeholder="e.g., This is an Abbott EPD product"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Product Identifiers */}
                  <div className="admin-form-section product-identifiers">
                    <div className="admin-section-title product-identifiers">
                      <i className="bi bi-upc-scan"></i>
                      <h6 className="mb-0">Product Identifiers</h6>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-3">
                        <label className="form-label fw-semibold">
                          <i className="bi bi-hash me-2"></i>Serial Number
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="serial"
                          value={productForm.serial}
                          onChange={handleProductChange}
                          required
                          placeholder="Enter serial number"
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fw-semibold">
                          <i className="bi bi-upc me-2"></i>GTIN
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="gtin"
                          value={productForm.gtin}
                          onChange={handleProductChange}
                          required
                          placeholder="Enter GTIN"
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fw-semibold">
                          <i className="bi bi-123 me-2"></i>Lot Number
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="lot"
                          value={productForm.lot}
                          onChange={handleProductChange}
                          required
                          placeholder="Enter lot number"
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fw-semibold">
                          <i className="bi bi-calendar-event me-2"></i>Expiration Date
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="expDate"
                          value={productForm.expDate}
                          onChange={handleProductChange}
                          placeholder="DD MMM YYYY"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Media & Links */}
                  <div className="admin-form-section media-links">
                    <div className="admin-section-title media-links">
                      <i className="bi bi-images"></i>
                      <h6 className="mb-0">Media & Links</h6>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-12">
                        <label className="form-label fw-semibold">
                          <i className="bi bi-image me-2"></i>Product Image
                        </label>
                        <input
                          type="file"
                          className="form-control"
                          name="image"
                          onChange={handleProductChange}
                          accept="image/*"
                        />
                        {productForm.image && (
                          <div className="admin-image-preview">
                            <img
                              src={productForm.image}
                              alt="Product Preview"
                            />
                          </div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          <i className="bi bi-globe me-2"></i>Product Page URL
                        </label>
                        <div className="input-group admin-url-input">
                          <span className="input-group-text">
                            <i className="bi bi-link-45deg"></i>
                          </span>
                          <input
                            type="url"
                            className="form-control"
                            name="productPage"
                            value={productForm.productPage}
                            onChange={handleProductChange}
                            placeholder="https://example.com/product"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          <i className="bi bi-file-pdf me-2"></i>Leaflet URL
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-light">
                            <i className="bi bi-file-earmark-text text-primary"></i>
                          </span>
                          <input
                            type="url"
                            className="form-control"
                            name="leaflet"
                            value={productForm.leaflet}
                            onChange={handleProductChange}
                            placeholder="https://example.com/leaflet"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <button 
                      type="reset" 
                      className="btn btn-light btn-lg admin-reset-button"
                    >
                      <i className="bi bi-x-circle me-2"></i>
                      Clear Form
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-primary btn-lg admin-submit-button"
                    >
                      <i className="bi bi-check-circle me-2"></i>
                      {productForm.id ? 'Update Product' : 'Add Product'}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Products Table */}
            <div className="card shadow-sm">
              <div className="card-header bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">Product List</h5>
                  <div className="input-group" style={{ width: '300px' }}>
                    <span className="input-group-text"><i className="bi bi-search"></i></span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search products..."
                      onChange={(e) => {
                        const searchTerm = e.target.value.toLowerCase();
                        const filtered = productList.filter(p => 
                          p.name.toString().toLowerCase().includes(searchTerm) ||
                          p.description.toString().toLowerCase().includes(searchTerm) ||
                          p.serial.toLowerCase().includes(searchTerm) ||
                          p.gtin.toLowerCase().includes(searchTerm)
                        );
                        setProducts(filtered);
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Image</th>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Serial</th>
                        <th>GTIN</th>
                        <th>Lot</th>
                        <th>Exp Date</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((product, index) => (
                        <tr key={index}>
                          <td>
                            <img
                              src={product.image}
                              alt={product.name}
                              style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                              className="rounded"
                            />
                          </td>
                          <td>{product.name}</td>
                          <td>{product.description}</td>
                          <td>{product.serial}</td>
                          <td>{product.gtin}</td>
                          <td>{product.lot}</td>
                          <td>{product.expDate}</td>
                          <td>
                            <span className={`badge ${product.verified ? 'bg-success' : 'bg-danger'}`}>
                              {product.verified ? 'Verified' : 'Unverified'}
                            </span>
                          </td>
                          <td>
                            <div className="btn-group">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleEdit(index)}
                                title="Edit"
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDelete(index)}
                                title="Delete"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                              <a
                                href={product.productPage}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-info"
                                title="View Product Page"
                              >
                                <i className="bi bi-box-arrow-up-right"></i>
                              </a>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {products.length === 0 && (
                        <tr>
                          <td colSpan="9" className="text-center py-4">
                            <i className="bi bi-inbox display-4 d-block mb-2"></i>
                            No products found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === "qrcode" && (
          <div>
            <h2 className="mb-4">QR Code Management</h2>
            
            {/* Company Selector */}
            <div className="card mb-4 admin-card admin-company-selector">
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-building fs-4 text-primary me-2"></i>
                      <label className="form-label fw-bold mb-0">Select Company</label>
                    </div>
                    <select 
                      className="form-select form-select-lg shadow-sm"
                      value={selectedCompany.id}
                      onChange={(e) => {
                        const company = companies.find(c => c.id === parseInt(e.target.value));
                        setSelectedCompany(company);
                        setQrCodeForm(prev => ({
                          ...prev,
                          companyId: company.id,
                          productId: ""
                        }));
                      }}
                    >
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6 text-md-end mt-4 mt-md-0">
                    <div className="d-flex align-items-center justify-content-md-end">
                      {selectedCompany.logo && (
                        <img 
                          src={selectedCompany.logo} 
                          alt={selectedCompany.name} 
                          className="admin-dashboard-company-logo"
                          style={{ height: '45px', width: 'auto' }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end mb-4">
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={() => setShowLinkQrSection(!showLinkQrSection)}
              >
                <i className={`bi ${showLinkQrSection ? 'bi-dash-circle' : 'bi-plus-circle'} me-2`}></i>
                {showLinkQrSection ? 'Hide Link Form' : 'Link New QR Code to Product'}
              </button>
            </div>

            {showLinkQrSection && (
              <div className="card mb-4 shadow-sm text-start">
                <div className="card-header bg-white py-3">
                  <h5 className="mb-0 text-start">Link QR Code to Product</h5>
                </div>
                <div className="card-body">
                <form onSubmit={(e) => {
                  e.preventDefault();
                  if (qrCodeForm.qrImage && qrCodeForm.productId) {
                    // Find the selected product
                    const selectedProduct = products.find(p => p.id.toString() === qrCodeForm.productId.toString());
                    
                    if (selectedProduct) {
                      // Create verification URL based on code type and product details
                      let verificationUrl;
                      
                      if (qrCodeForm.codeType === "qrcode") {
                        // QR code format
                        verificationUrl = `https://abbott-product-verification.h2cloud.it/Product/verify/01/${selectedProduct.gtin}`;
                      } else {
                        // Data Matrix format - includes lot, serial, and expiry date
                        // Convert expiry date to format YYMMDD
                        const expDate = selectedProduct.expDate;
                        let formattedExpDate = "";
                        
                        if (expDate) {
                          // Try to parse the date from format like "24 Aug 2027"
                          try {
                            const dateParts = expDate.split(" ");
                            const day = dateParts[0];
                            const month = dateParts[1];
                            const year = dateParts[2];
                            
                            // Convert month name to month number
                            const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                            const monthIndex = monthNames.findIndex(m => m === month);
                            
                            if (monthIndex !== -1) {
                              // Format as YYMMDD
                              const yearShort = year.substring(2); // Get last 2 digits
                              const monthNum = (monthIndex + 1).toString().padStart(2, '0');
                              formattedExpDate = yearShort + monthNum + day.padStart(2, '0');
                            }
                          } catch (e) {
                            // If parsing fails, use the expiry date as is
                            formattedExpDate = "270824"; // Default fallback
                          }
                        } else {
                          formattedExpDate = "270824"; // Default fallback
                        }
                        
                        verificationUrl = `https://abbott-product-verification.h2cloud.it/Product/verify/01/${selectedProduct.gtin}/10/${selectedProduct.lot}/21/${selectedProduct.serial}?17=${formattedExpDate}`;
                      }
                      
                      const newQrCode = {
                        id: Date.now(),
                        qrImage: qrCodeForm.qrImage,
                        productId: selectedProduct.id,
                        productName: selectedProduct.name,
                        productImage: selectedProduct.image,
                        companyId: selectedCompany.id,
                        companyName: selectedCompany.name,
                        codeType: qrCodeForm.codeType,
                        verificationUrl: verificationUrl,
                        dateAdded: new Date().toISOString()
                      };
                      
                      setQrCodes([...qrCodes, newQrCode]);
                      setQrCodeForm({
                        qrImage: "",
                        productId: "",
                        companyId: selectedCompany.id,
                        codeType: qrCodeForm.codeType
                      });
                    }
                  }
                }}>
                  <div className="row g-4 mb-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        <i className="bi bi-upc-scan me-2"></i>Code Type
                      </label>
                      <select
                        className="form-select mb-3"
                        value={qrCodeForm.codeType}
                        onChange={(e) => setQrCodeForm(prev => ({ ...prev, codeType: e.target.value }))}
                      >
                        <option value="qrcode">QR Code</option>
                        <option value="datamatrix">Data Matrix</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        <i className="bi bi-box-seam me-2"></i>Select Product
                      </label>
                      <select
                        className="form-select"
                        value={qrCodeForm.productId}
                        onChange={(e) => setQrCodeForm(prev => ({ ...prev, productId: e.target.value }))}
                        required
                      >
                        <option value="">-- Select a product --</option>
                        {filteredProducts.map(product => (
                          <option key={product.id} value={product.id}>
                            {typeof product.name === 'string' ? product.name : 'Product ' + product.id} - GTIN: {product.gtin}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="row g-4 mb-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        <i className="bi bi-qr-code me-2"></i>QR Code Image
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setQrCodeForm(prev => ({
                                ...prev,
                                qrImage: reader.result
                              }));
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        required
                      />
                      {qrCodeForm.qrImage && (
                        <div className="mt-3 text-center">
                          <img
                            src={qrCodeForm.qrImage}
                            alt="QR Code Preview"
                            style={{ maxWidth: '200px', maxHeight: '200px' }}
                            className="border p-2"
                          />
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      
                      {qrCodeForm.productId && (
                        <div className="mt-3">
                          <div className="card">
                            <div className="card-body p-3">
                              <div className="d-flex align-items-center">
                                {(() => {
                                  const selectedProduct = products.find(p => p.id.toString() === qrCodeForm.productId.toString());
                                  if (selectedProduct) {
                                    return (
                                      <>
                                        <div className="me-3">
                                          <img 
                                            src={selectedProduct.image} 
                                            alt={selectedProduct.name}
                                            style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                            className="rounded"
                                          />
                                        </div>
                                        <div>
                                          <h6 className="mb-1">{typeof selectedProduct.name === 'string' ? selectedProduct.name : 'Product ' + selectedProduct.id}</h6>
                                          <div className="small text-muted mb-1">GTIN: {selectedProduct.gtin}</div>
                                          <div className="small text-muted">Verification URL will be: <br />
                                            {qrCodeForm.codeType === "qrcode" ? (
                                              <span className="text-primary">https://abbott-product-verification.h2cloud.it/Product/verify/01/{selectedProduct.gtin}</span>
                                            ) : (
                                              <span className="text-primary">https://abbott-product-verification.h2cloud.it/Product/verify/01/{selectedProduct.gtin}/10/{selectedProduct.lot}/21/{selectedProduct.serial}?17=...</span>
                                            )}
                                          </div>
                                        </div>
                                      </>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="d-flex justify-content-end">
                    <button type="submit" className="btn btn-primary">
                      <i className="bi bi-link-45deg me-2"></i>
                      Link QR Code to Product
                    </button>
                  </div>
                </form>
              </div>
            </div>
            )}

            <div className="card shadow-sm">
              <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0">QR Code Mappings</h5>
                <span className="badge bg-primary">{qrCodes.length} QR Codes</span>
              </div>
              <div className="card-body border-bottom pb-3">
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="input-group">
                      <span className="input-group-text bg-light">
                        <i className="bi bi-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by product name, company, or URL..."
                        value={qrCodeSearch}
                        onChange={(e) => setQrCodeSearch(e.target.value)}
                      />
                      {qrCodeSearch && (
                        <button 
                          className="btn btn-outline-secondary" 
                          type="button"
                          onClick={() => setQrCodeSearch("")}
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex align-items-center justify-content-md-end">
                      <label className="me-2 text-nowrap">Filter by type:</label>
                      <select
                        className="form-select form-select-sm w-auto"
                        value={qrCodeFilter}
                        onChange={(e) => setQrCodeFilter(e.target.value)}
                      >
                        <option value="all">All Types</option>
                        <option value="qrcode">QR Code Only</option>
                        <option value="datamatrix">Data Matrix Only</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-body">
                {qrCodes.length > 0 ? (
                  // Filter QR codes based on search and type filter
                  (() => {
                    // Apply filters
                    const filteredQrCodes = qrCodes.filter(qrCode => {
                      // Apply code type filter
                      if (qrCodeFilter !== "all" && qrCode.codeType !== qrCodeFilter) {
                        return false;
                      }
                      
                      // Apply search filter if search text exists
                      if (qrCodeSearch) {
                        const searchLower = qrCodeSearch.toLowerCase();
                        return (
                          // Search in product name
                          (typeof qrCode.productName === 'string' && qrCode.productName.toLowerCase().includes(searchLower)) ||
                          // Search in company name
                          qrCode.companyName.toLowerCase().includes(searchLower) ||
                          // Search in URL
                          qrCode.verificationUrl.toLowerCase().includes(searchLower)
                        );
                      }
                      
                      return true;
                    });
                    
                    if (filteredQrCodes.length === 0) {
                      return (
                        <div className="text-center py-5">
                          <i className="bi bi-search display-4 text-muted mb-3"></i>
                          <p className="text-muted">No QR codes match your search criteria.</p>
                          <button 
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => {
                              setQrCodeSearch("");
                              setQrCodeFilter("all");
                            }}
                          >
                            Clear Filters
                          </button>
                        </div>
                      );
                    }
                    
                    return (
                  <div className="table-responsive">
                    <table className="table table-hover text-start">
                      <thead>
                        <tr>
                          <th>Code Image</th>
                          <th>Company</th>
                          <th>Code Type</th>
                          <th>Product</th>
                          <th>Verification URL</th>
                          <th>Date Added</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {qrCodes
                          .filter(qrCode => {
                            // Apply code type filter
                            if (qrCodeFilter !== "all" && qrCode.codeType !== qrCodeFilter) {
                              return false;
                            }
                            
                            // Apply search filter if search text exists
                            if (qrCodeSearch) {
                              const searchLower = qrCodeSearch.toLowerCase();
                              return (
                                // Search in product name
                                (typeof qrCode.productName === 'string' && qrCode.productName.toLowerCase().includes(searchLower)) ||
                                // Search in company name
                                qrCode.companyName.toLowerCase().includes(searchLower) ||
                                // Search in URL
                                qrCode.verificationUrl.toLowerCase().includes(searchLower)
                              );
                            }
                            
                            return true;
                          })
                          .map(qrCode => (
                          <tr key={qrCode.id}>
                            <td>
                              <img
                                src={qrCode.qrImage}
                                alt="QR Code"
                                style={{ width: '60px', height: '60px', cursor: 'pointer' }}
                                className="border"
                                onClick={() => {
                                  setModalImage(qrCode.qrImage);
                                  setShowModal(true);
                                }}
                                title="Click to enlarge"
                              />
                            </td>
                            <td>{qrCode.companyName}</td>
                            <td>
                              <span className={`badge ${qrCode.codeType === "qrcode" ? "bg-info" : "bg-warning"}`}>
                                {qrCode.codeType === "qrcode" ? "QR Code" : "Data Matrix"}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <img 
                                  src={qrCode.productImage} 
                                  alt={qrCode.productName}
                                  style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                  className="rounded me-2"
                                />
                                <span>{qrCode.productName}</span>
                              </div>
                            </td>
                            <td>
                              <a href={qrCode.verificationUrl} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                                <div className="text-truncate" title={qrCode.verificationUrl}>
                                  {qrCode.verificationUrl}
                                </div>
                              </a>
                            </td>
                            <td>
                              {new Date(qrCode.dateAdded).toLocaleDateString()}
                            </td>
                            <td>
                              <div className="btn-group">
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => {
                                    if (window.confirm('Are you sure you want to delete this QR code mapping?')) {
                                      setQrCodes(qrCodes.filter(qr => qr.id !== qrCode.id));
                                    }
                                  }}
                                  title="Delete"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div>
                        <small className="text-muted">
                          {qrCodes
                            .filter(qrCode => {
                              // Apply code type filter
                              if (qrCodeFilter !== "all" && qrCode.codeType !== qrCodeFilter) {
                                return false;
                              }
                              
                              // Apply search filter if search text exists
                              if (qrCodeSearch) {
                                const searchLower = qrCodeSearch.toLowerCase();
                                return (
                                  // Search in product name
                                  (typeof qrCode.productName === 'string' && qrCode.productName.toLowerCase().includes(searchLower)) ||
                                  // Search in company name
                                  qrCode.companyName.toLowerCase().includes(searchLower) ||
                                  // Search in URL
                                  qrCode.verificationUrl.toLowerCase().includes(searchLower)
                                );
                              }
                              
                              return true;
                            }).length
                          } of {qrCodes.length} QR codes shown
                        </small>
                      </div>
                      {(qrCodeSearch || qrCodeFilter !== "all") && (
                        <button 
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => {
                            setQrCodeSearch("");
                            setQrCodeFilter("all");
                          }}
                        >
                          <i className="bi bi-x-circle me-1"></i>
                          Clear Filters
                        </button>
                      )}
                    </div>
                  </div>
                  );
                })()
                ) : (
                  <div className="text-center py-5">
                    <i className="bi bi-qr-code display-4 text-muted mb-3"></i>
                    <p className="text-muted">No QR codes linked to products yet. Use the form above to link a QR code to a product.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeSection === "theme" && (
          <div>
            <h2 className="mb-4">Configuration</h2>
            
            <div className="d-flex justify-content-end mb-4">
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={() => setShowConfigForm(!showConfigForm)}
              >
                <i className={`bi ${showConfigForm ? 'bi-dash-circle' : 'bi-plus-circle'} me-2`}></i>
                {showConfigForm ? 'Hide Configuration Form' : 'Add New Configuration'}
              </button>
            </div>
            
            {showConfigForm && (
              <div className="card mb-4 shadow-sm">
                <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Company Settings</h5>
                  {editingConfigId ? (
                    <span className="badge bg-primary">Editing Configuration</span>
                  ) : (
                    <span className="badge bg-success">New Configuration</span>
                  )}
                </div>
                <div className="card-body">
                <form onSubmit={handleConfigSubmit} className="needs-validation">
                  <div className="row g-4 mb-3 pb-3 border-bottom">
                    <h6 className="text-muted mb-3">Basic Information</h6>
                    <div className="col-md-4">
                      <label className="form-label text-start w-100">Company Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="companyName"
                        value={configForm.companyName}
                        onChange={handleConfigFormChange}
                        placeholder="Enter company name"
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-start w-100">URL</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          name="url"
                          value={configForm.url}
                          onChange={handleConfigFormChange}
                          placeholder="Enter subdomain"
                          required
                        />
                        <span className="input-group-text">.h2cloud.it</span>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-start w-100">Theme Color</label>
                      <div className="d-flex align-items-center gap-2">
                        <select 
                          className="form-select"
                          name="themeColor"
                          value={configForm.themeColor}
                          onChange={handleConfigFormChange}
                          required
                        >
                          <option value="#0d6efd">Blue</option>
                          <option value="#dc3545">Red</option>
                          <option value="#198754">Green</option>
                          <option value="#6f42c1">Purple</option>
                          <option value="#fd7e14">Orange</option>
                          <option value="#20c997">Teal</option>
                        </select>
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            backgroundColor: configForm.themeColor,
                            border: "2px solid #dee2e6",
                            borderRadius: "4px",
                            flexShrink: 0
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="row g-4 mt-2">
                    <h6 className="text-muted mb-3">Branding & Visuals</h6>
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-header bg-light py-2">
                          <label className="form-label text-start w-100 mb-0">Company Logo</label>
                        </div>
                        <div className="card-body">
                          <div className="d-flex gap-2 mb-2">
                            <div className="flex-grow-1">
                              <input
                                type="text"
                                className="form-control"
                                name="logo"
                                value={configForm.logo}
                                onChange={handleConfigFormChange}
                                placeholder="Enter logo URL"
                              />
                            </div>
                            <div className="d-flex align-items-center">
                              <span className="text-muted me-2">or</span>
                              <label className="btn btn-outline-secondary mb-0">
                                <i className="bi bi-upload me-1"></i> Browse
                                <input
                                  type="file"
                                  className="d-none"
                                  accept="image/*"
                                  onChange={handleLogoFileChange}
                                />
                              </label>
                            </div>
                          </div>
                          {configForm.logo ? (
                            <div className="mt-3 text-center p-3 bg-light rounded">
                              <img 
                                src={configForm.logo} 
                                alt="Logo preview" 
                                style={{ maxHeight: '60px' }} 
                                className="mb-2" 
                              />
                              <div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => setConfigForm(prev => ({ ...prev, logo: "" }))}
                                >
                                  <i className="bi bi-trash me-1"></i> Remove
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-3 text-center p-3 bg-light rounded text-muted">
                              <i className="bi bi-image display-6"></i>
                              <p className="mb-0 small">Logo preview will appear here</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-header bg-light py-2">
                          <label className="form-label text-start w-100 mb-0">Background Image</label>
                        </div>
                        <div className="card-body">
                          <div className="d-flex gap-2 mb-2">
                            <div className="flex-grow-1">
                              <input
                                type="text"
                                className="form-control"
                                name="background"
                                value={configForm.background}
                                onChange={handleConfigFormChange}
                                placeholder="Enter background image URL"
                              />
                            </div>
                            <div className="d-flex align-items-center">
                              <span className="text-muted me-2">or</span>
                              <label className="btn btn-outline-secondary mb-0">
                                <i className="bi bi-upload me-1"></i> Browse
                                <input
                                  type="file"
                                  className="d-none"
                                  accept="image/*"
                                  onChange={handleBackgroundFileChange}
                                />
                              </label>
                            </div>
                          </div>
                          {configForm.background ? (
                            <div className="mt-3 text-center p-3 bg-light rounded">
                              <img 
                                src={configForm.background} 
                                alt="Background preview" 
                                style={{ height: '60px', maxWidth: '100%', objectFit: 'contain' }} 
                                className="mb-2" 
                              />
                              <div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => setConfigForm(prev => ({ ...prev, background: "" }))}
                                >
                                  <i className="bi bi-trash me-1"></i> Remove
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-3 text-center p-3 bg-light rounded text-muted">
                              <i className="bi bi-card-image display-6"></i>
                              <p className="mb-0 small">Background preview will appear here</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-top d-flex justify-content-between align-items-center">
                    <div>
                      <button type="submit" className="btn btn-primary">
                        <i className={`bi ${editingConfigId ? 'bi-save' : 'bi-plus-circle'} me-1`}></i>
                        {editingConfigId ? 'Update' : 'Add'} Configuration
                      </button>
                      {editingConfigId && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary ms-2"
                          onClick={() => {
                            setConfigForm({
                              companyName: "",
                              themeColor: "#0d6efd",
                              url: "",
                              logo: "",
                              background: ""
                            });
                            setEditingConfigId(null);
                          }}
                        >
                          <i className="bi bi-x-circle me-1"></i>
                          Cancel
                        </button>
                      )}
                    </div>
                    <div className="text-muted small">
                      {editingConfigId ? 'Editing existing configuration' : 'Creating new configuration'}
                    </div>
                  </div>
                </form>
              </div>
            </div>
            )}

            <div className="card shadow-sm">
              <div className="card-header bg-white py-3">
                <h5 className="mb-0">Saved Configurations</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover text-start">
                    <thead>
                      <tr>
                        <th style={{ width: '15%', textAlign: 'left' }}>Logo</th>
                        <th style={{ width: '20%', textAlign: 'left' }}>Company Name</th>
                        <th style={{ width: '30%', textAlign: 'left' }}>URL</th>
                        <th style={{ width: '15%', textAlign: 'left' }}>Theme Color</th>
                        <th style={{ width: '20%', textAlign: 'left' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {configurations.map((config) => (
                        <tr key={config.id}>
                          <td style={{ textAlign: 'left' }}>
                            {config.logo ? (
                              <img 
                                src={config.logo} 
                                alt={`${config.companyName} logo`} 
                                style={{ maxHeight: '40px' }} 
                              />
                            ) : (
                              <span className="text-muted">No logo</span>
                            )}
                          </td>
                          <td style={{ textAlign: 'left' }}>{config.companyName}</td>
                          <td style={{ textAlign: 'left' }}>
                            <a href={`https://${config.url}`} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                              {config.url}
                            </a>
                          </td>
                          <td style={{ textAlign: 'left' }}>
                            <div className="d-flex align-items-center gap-2">
                              <div
                                style={{
                                  width: "30px",
                                  height: "30px",
                                  backgroundColor: config.themeColor,
                                  border: "2px solid #dee2e6",
                                  borderRadius: "4px"
                                }}
                              />
                              <span className="text-muted">{config.themeColor}</span>
                            </div>
                          </td>
                          <td style={{ textAlign: 'left' }}>
                            <div className="btn-group">
                              <button 
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleConfigEdit(config)}
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button 
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleConfigDelete(config.id)}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    
    {/* QR Code Modal */}
    {showModal && (
      <div className="qr-code-modal-container">
        <div className="modal show d-block">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-white">Scan QR Code</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body text-center p-5">
                <img
                  src={modalImage}
                  alt="QR Code"
                  style={{ maxWidth: '100%', maxHeight: '400px' }}
                  className="border"
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop show" onClick={() => setShowModal(false)}></div>
      </div>
    )}
    </>
  );
};

export default AdminDashboard;
